/**
 * Workspace Cloud client — CRUD against `public.workspace_views`.
 *
 * RLS does the heavy lifting: tenant scoping is implicit (all selects are
 * filtered by tenant_id via the `current_tenant_id()` JWT helper) and writes
 * stamp `created_by = auth.uid()`. The client just round-trips snake_case rows
 * through the schema.
 */
import { supabase } from "@/integrations/supabase/client";
import { workspaceViewSchema } from "@/workspace/schema";
import type { WorkspaceView, WorkspaceViewDraft } from "@/workspace/types";
import {
  diffWorkspaceNavConfig,
  hasNavConfigChanges,
  recordWorkspaceActivity,
  summarizeNavDiff,
} from "@/services/workspaceActivityLog";

interface DbRow {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  role_name: string | null;
  is_default: boolean;
  landing_path: string;
  nav_items: unknown;
  dashboard_widgets: unknown;
  page_defaults: unknown;
  copy_preset_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Translate Postgres validation-trigger errors (raised with SQLSTATE 22023 by
 * `validate_workspace_nav_items`) into a user-friendly Error so callers can
 * surface them via toasts. Non-validation errors pass through unchanged.
 */
function mapNavValidationError(error: { code?: string; message?: string }): Error {
  const isValidationError =
    error?.code === "22023" || /nav item|nav_items/i.test(error?.message ?? "");
  if (isValidationError) {
    const msg = error.message ?? "Invalid navigation configuration";
    return new Error(`Navigation validation failed: ${msg}`);
  }
  return error as unknown as Error;
}

function rowToWorkspace(row: DbRow): WorkspaceView | null {
  const parsed = workspaceViewSchema.safeParse({
    id: row.id,
    tenantId: row.tenant_id,
    name: row.name,
    description: row.description,
    roleName: row.role_name,
    isDefault: row.is_default,
    landingPath: row.landing_path,
    navItems: Array.isArray(row.nav_items) ? row.nav_items : [],
    dashboardWidgets: Array.isArray(row.dashboard_widgets) ? row.dashboard_widgets : [],
    pageDefaults:
      row.page_defaults && typeof row.page_defaults === "object" && !Array.isArray(row.page_defaults)
        ? (row.page_defaults as Record<string, string>)
        : {},
    copyPresetId: row.copy_preset_id,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  });
  if (!parsed.success) {
    console.warn("[workspace] dropped malformed row", row.id, parsed.error.flatten());
    return null;
  }
  return parsed.data as WorkspaceView;
}

export async function listWorkspaces(): Promise<WorkspaceView[]> {
  const { data, error } = await supabase
    .from("workspace_views")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as DbRow[]).map(rowToWorkspace).filter((w): w is WorkspaceView => w !== null);
}

export async function createWorkspace(
  draft: WorkspaceViewDraft,
  tenantId: string,
  userId: string,
): Promise<WorkspaceView> {
  const insertPayload = {
    tenant_id: tenantId,
    name: draft.name,
    description: draft.description,
    role_name: draft.roleName,
    is_default: draft.isDefault,
    landing_path: draft.landingPath,
    // jsonb columns — cast through unknown so the generated `Json` type accepts our shape.
    nav_items: draft.navItems as unknown as never,
    dashboard_widgets: draft.dashboardWidgets as unknown as never,
    page_defaults: draft.pageDefaults as unknown as never,
    copy_preset_id: draft.copyPresetId,
    created_by: userId,
  };
  const { data, error } = await supabase
    .from("workspace_views")
    .insert([insertPayload])
    .select("*")
    .single();
  if (error) throw mapNavValidationError(error);
  const parsed = rowToWorkspace(data as DbRow);
  if (!parsed) throw new Error("Workspace was saved but failed validation on read-back.");
  return parsed;
}

export async function updateWorkspace(
  id: string,
  draft: WorkspaceViewDraft,
): Promise<WorkspaceView> {
  // Snapshot the prior state so we can diff nav-affecting fields after the
  // write succeeds. RLS scoping means a missing/forbidden row simply returns
  // null here — the subsequent update will fail in that case and we never
  // emit a misleading audit entry.
  const { data: prevRow } = await supabase
    .from("workspace_views")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  const previous = prevRow ? rowToWorkspace(prevRow as DbRow) : null;

  const updatePayload = {
    name: draft.name,
    description: draft.description,
    role_name: draft.roleName,
    is_default: draft.isDefault,
    landing_path: draft.landingPath,
    nav_items: draft.navItems as unknown as never,
    dashboard_widgets: draft.dashboardWidgets as unknown as never,
    page_defaults: draft.pageDefaults as unknown as never,
    copy_preset_id: draft.copyPresetId,
  };
  const { data, error } = await supabase
    .from("workspace_views")
    .update(updatePayload)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw mapNavValidationError(error);
  const parsed = rowToWorkspace(data as DbRow);
  if (!parsed) throw new Error("Workspace was saved but failed validation on read-back.");

  // Audit log: only nav-affecting changes are recorded here. Widget /
  // page-default edits are intentionally excluded so the nav governance
  // stream stays focused. Failures are swallowed — audit logging must
  // never block a successful save.
  if (previous) {
    try {
      const diff = diffWorkspaceNavConfig(previous, draft);
      if (hasNavConfigChanges(diff)) {
        recordWorkspaceActivity({
          workspaceId: id,
          action: "updated",
          summary: summarizeNavDiff(diff),
          diff,
        });
      }
    } catch (e) {
      console.warn("[workspace] failed to record audit entry", e);
    }
  }

  return parsed;
}

export async function deleteWorkspace(id: string): Promise<void> {
  const { error } = await supabase.from("workspace_views").delete().eq("id", id);
  if (error) throw error;
}
